import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import {
  selectFilters,
  selectGridState,
} from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { selectUserGridPreference } from 'oidc/UserDevicePreference/userPreference.selectors';
import { buildPlacementPayPackagesPayload } from './helpers';

interface UsePlacementPayPackagesFetchContext {
  orderId?: number;
  facilityId?: number;
  candidateId?: number;
}

function getGridTag(context: UsePlacementPayPackagesFetchContext): string {
  if (context.orderId) return `placement-pay-packages-grid-${context.orderId}`;
  if (context.facilityId) return `facility-placement-pay-packages-grid-${context.facilityId}`;
  if (context.candidateId) return `candidate-placement-pay-packages-grid-${context.candidateId}`;
  return 'placement-pay-packages-grid';
}

/** Hook to build and dispatch Placement Pay Packages fetch with server-side pagination, sort, filters */
export function usePlacementPayPackagesFetch(context: UsePlacementPayPackagesFetchContext) {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const gridTag = getGridTag(context);
  const gridPrefs = useSelector(selectUserGridPreference).find(p => p.id === gridTag);

  const sortModel = gridPrefs?.value?.multiSortedColumns
    ? gridPrefs.value.multiSortedColumns.map((s: { column: string; direction: string }) => ({
        field: s.column,
        sort: s.direction,
      }))
    : undefined;

  const fetch = useCallback(
    (
      overrides?: {
        pageNumber?: number;
        pageSize?: number;
        sortModel?: { field: string; sort: string }[];
        filters?: Record<string, any>;
        selectedChipFilters?: Record<string, any>;
      },
    ) => {
      const pageNumber = overrides?.pageNumber ?? gridState?.pageNumber ?? 1;
      const pageSize = overrides?.pageSize ?? gridState?.pageSize ?? 20;
      const sort = overrides?.sortModel ?? sortModel;
      const filtersToUse = overrides?.filters ?? filters?.filters ?? {};
      const selectedChipFiltersToUse =
        overrides?.selectedChipFilters ?? filters?.selectedChipFilters ?? {};

      const payload = buildPlacementPayPackagesPayload(
        {
          orderId: context.orderId ?? undefined,
          candidateId: context.candidateId ?? undefined,
          facilityId: context.facilityId ?? undefined,
        },
        filtersToUse,
        selectedChipFiltersToUse,
        pageNumber,
        pageSize,
        sort,
      );

      dispatch(orderDataActions.getPlacementPayPackages(payload));
    },
    [
      context.orderId,
      context.facilityId,
      context.candidateId,
      filters?.filters,
      filters?.selectedChipFilters,
      gridState?.pageNumber,
      gridState?.pageSize,
      sortModel,
      dispatch,
    ],
  );

  return fetch;
}
